import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import logger from "./logger";

export type RtkError = FetchBaseQueryError | SerializedError;

function parseStatus(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

const STATUS_KEYS = [
  "status",
  "originalStatus",
  "statusCode",
  "httpStatus",
  "code",
] as const;

/** Returns HTTP status from RTK Query error if present (e.g. 403, 404). Handles number or string and multiple error shapes. */
export const getStatusFromError = (error?: RtkError): number | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const e = error as Record<string, unknown>;
  for (const key of STATUS_KEYS) {
    if (e[key] != null) {
      const parsed = parseStatus(e[key]);
      if (parsed != null && parsed >= 400 && parsed < 600) return parsed;
    }
  }
  const data = e.data as Record<string, unknown> | undefined;
  if (data && typeof data === "object") {
    for (const key of STATUS_KEYS) {
      if (data[key] != null) {
        const parsed = parseStatus(data[key]);
        if (parsed != null && parsed >= 400 && parsed < 600) return parsed;
      }
    }
  }
  const response = e.response as Record<string, unknown> | undefined;
  if (response && typeof response === "object" && response.status != null)
    return parseStatus(response.status);
  return undefined;
};

/**
 * Extracts a validation error message from the service.
 *
 * @param error an error returned by a service
 * @param fallback a fallback message
 */
export const getMessageFromError = (
  error?: RtkError,
  fallback = ""
): string => {
  if (getStatusFromError(error) === 404) {
    return fallback;
  }
  if (error && "data" in error) {
    try {
      if (typeof error.data === "string") {
        return error.data || fallback;
      }

      const parsed = JSON.parse(JSON.stringify(error.data)) as {
        error?: {
          uiDetails?: { userErrorMessage?: string };
          details?: {
            validationError?: string[];
            error?: string;
            message?: string;
          };
        };
      };
      const uiMessage = parsed.error?.uiDetails?.userErrorMessage;
      const validation = parsed.error?.details?.validationError?.[0];
      const detailsError =
        typeof parsed.error?.details?.error === "string"
          ? parsed.error.details.error
          : undefined;
      const detailsMessage =
        typeof parsed.error?.details?.message === "string"
          ? parsed.error.details.message
          : undefined;
      return (
        uiMessage || validation || detailsError || detailsMessage || fallback
      );
    } catch (err) {
      logger.log(err);
      // fall though
    }
  }
  return fallback;
};

/**
 * Checks if an error is a chunk loading error (typically from dynamic imports).
 * These errors occur when the browser fails to fetch a dynamically imported module,
 * often due to network issues or after app deployments.
 *
 * @param error The error to check
 * @returns true if the error is a chunk loading error
 */
export const isChunkLoadError = (error: unknown): boolean => {
  return (
    error instanceof Error &&
    (error.message.includes("Failed to fetch") ||
      error.message.includes("dynamically imported module") ||
      error.message.includes("Loading chunk"))
  );
};

/**
 * Retries a dynamic import with exponential backoff.
 * This handles transient network errors during chunk loading.
 *
 * @param importFn The import function to retry
 * @param retries Number of retry attempts (default: 3)
 * @param delay Initial delay in ms (default: 1000)
 * @returns The result of the import function
 * @throws The original error if max retries exceeded or if error is not a chunk loading error
 */
export const retryImport = async <T>(
  importFn: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> => {
  try {
    return await importFn();
  } catch (error) {
    if (retries <= 0) {
      throw error;
    }

    // Only retry on network/chunk loading errors
    if (!isChunkLoadError(error)) {
      throw error;
    }

    // Wait before retrying with exponential backoff
    await new Promise((resolve) => setTimeout(resolve, delay));
    return retryImport(importFn, retries - 1, delay * 2);
  }
};
